import React from 'react'
import { ActivityIndicator, Pressable, Switch, Text, View } from 'react-native'

import { BaseModal } from '../../ui/BaseModal'
import { usePrivacyDataControls } from '../../../src/privacy/hooks/usePrivacyDataControls'
import { privacyDataControlsModalStyles as styles } from './PrivacyDataControlsModal.styles'
import { theme } from '../../../theme'
import { LanguageContext } from '../../../src/i18n'

interface PrivacyDataControlsModalProps {
  isVisible: boolean
  onDismiss: () => void
  /** Opens the account-deletion flow (App Store Guideline 5.1.1(v): deletion must be discoverable here). */
  onOpenAccountDeletion: () => void
  testID?: string
}

export function PrivacyDataControlsModal({ isVisible, onDismiss, onOpenAccountDeletion, testID }: PrivacyDataControlsModalProps) {
  const languageContext = React.useContext(LanguageContext)
  if (!languageContext) {
    throw new Error('PrivacyDataControlsModal must be used within LanguageProvider')
  }
  const { t } = languageContext
  const {
    isReady,
    marketingEmailOptIn,
    isUpdatingMarketing,
    isExportingData,
    dataExportNextAvailableAt,
    webAnalyticsConsentEnabled,
    updateMarketingConsent,
    requestDataExport,
    updateWebAnalyticsConsent,
  } = usePrivacyDataControls()

  if (!isVisible) return null

  return (
    <BaseModal
      isVisible={isVisible}
      onDismiss={onDismiss}
      contentAlignment="center"
      contentContainerStyle={styles.modalOuter}
      testID={testID ?? 'privacy-data-controls-modal'}
      contentTestID={testID ? `${testID}-content` : 'privacy-data-controls-modal-content'}
    >
      <View style={styles.sheet}>
        <Text style={styles.title}>{t.privacyControls.title}</Text>
        <Text style={styles.subtitle}>{t.privacyControls.subtitle}</Text>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t.privacyControls.marketingEmailsTitle}</Text>
          <Text style={styles.sectionHint}>{t.privacyControls.marketingEmailsHint}</Text>
          <View style={styles.rowBetween}>
            <Text style={styles.sectionHint}>
              {marketingEmailOptIn ? t.privacyControls.enabled : t.privacyControls.disabled}
            </Text>
            {isReady ? (
              <Switch
                value={marketingEmailOptIn}
                onValueChange={(nextValue) => {
                  void updateMarketingConsent(nextValue)
                }}
                disabled={isUpdatingMarketing}
                trackColor={{ true: '#8B5CF6', false: '#374151' }}
              />
            ) : (
              <ActivityIndicator size="small" color={theme.primary} />
            )}
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t.privacyControls.analyticsTrackingTitle}</Text>
          <Text style={styles.sectionHint}>{t.privacyControls.analyticsTrackingHint}</Text>
          <View style={styles.rowBetween}>
            <Text style={styles.sectionHint}>
              {webAnalyticsConsentEnabled ? t.privacyControls.enabled : t.privacyControls.disabled}
            </Text>
            <Switch
              value={webAnalyticsConsentEnabled}
              onValueChange={(nextValue) => {
                void updateWebAnalyticsConsent(nextValue)
              }}
              trackColor={{ true: '#8B5CF6', false: '#374151' }}
            />
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t.privacyControls.accountDataCopyTitle}</Text>
          <Text style={styles.sectionHint}>
            {t.privacyControls.accountDataCopyHint}
          </Text>
          <Pressable
            style={({ pressed }) => [
              styles.exportButton,
              (isExportingData || !isReady) && styles.exportButtonDisabled,
              pressed && styles.exportButtonPressed,
            ]}
            onPress={() => {
              void requestDataExport()
            }}
            disabled={isExportingData || !isReady}
          >
            {isExportingData ? (
              <ActivityIndicator size="small" color={theme.white} />
            ) : (
              <Text style={styles.exportButtonText}>{t.privacyControls.downloadMyData}</Text>
            )}
          </Pressable>
          {dataExportNextAvailableAt ? (
            <Text style={styles.helperText}>
              {t.privacyControls.nextExportAvailable}: {dataExportNextAvailableAt.toLocaleString()}
            </Text>
          ) : null}
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t.privacyControls.deleteYourAccount}</Text>
          <Text style={styles.sectionHint}>{t.privacyControls.deleteYourAccountHint}</Text>
          <Pressable
            style={({ pressed }) => [styles.deleteButton, pressed && styles.deleteButtonPressed]}
            onPress={onOpenAccountDeletion}
            accessibilityRole="button"
            accessibilityLabel={t.privacyControls.deleteYourAccount}
            testID={testID ? `${testID}-delete-account` : 'privacy-data-controls-modal-delete-account'}
          >
            <Text style={styles.deleteButtonText}>{t.privacyControls.deleteYourAccount}</Text>
          </Pressable>
        </View>

        <Pressable
          style={({ pressed }) => [styles.closeButton, pressed && styles.closeButtonPressed]}
          onPress={onDismiss}
        >
          <Text style={styles.closeButtonText}>{t.privacyControls.close}</Text>
        </Pressable>
      </View>
    </BaseModal>
  )
}

